import { Injectable } from '@angular/core';
import { FormBuilder, FormControl, FormArray, FormGroup, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { businessDays, timeZones } from './configuration';
import { BusinessPhoneNumber, BusinessPhoneNumberType, Service, Address, Area, Business, AdditionalProperties, TransferDepartment, TransferDepartmentType, TransferDepartmentAvailabilityType, TextLink, BusinessHours, SpecialHours } from '../core/business/business';
import { areEqual } from '@rallycommerce/common/utils.js';
import { Agent, AgentKnowledge, AlternativeGreetingCustomHour, BackgroundNoiseType, CalendarAvailabilityType, CalendarConfig, CalendarCustomHour, CalendarDateOverride, CalendarDateOverrideType, CustomQuestion, MessageScenario, SmsWidgetAllowedDomain, TrainingFaq } from '../core/agent/agent';
import { PhoneNumber } from '../core/phone/phone';
import { formatPhoneNumber } from '../core/helpers/utils';
import { activeSpecialHours, todayInTimezone } from './business/special-hours/special-hours.utils';
import { HTTPS_URL_PATTERN, WEBSITE_URL_PATTERN } from '../shared/validators/website-url.validator';

@Injectable({
    providedIn: 'root'
})
export class ConfigurationHelperService {
    constructor(private fb: FormBuilder) { }

    createService(service: Service = { name: '', isActive: true }): FormGroup {
        return this.fb.group({
            name: [service.name, Validators.required],
            id: [service.id],
            isActive: [service.isActive === undefined ? true : service.isActive]
        });
    }

    createArea(area: Area = { name: '', isActive: true }): FormGroup {
        return this.fb.group({
            name: [area?.name],
            isActive: [area.isActive === undefined ? true : area.isActive]
        });
    }

    createAddress(address: Address = { street: '', city: '', state: '', zipCode: '' }): FormGroup {
        return this.fb.group({
            street: [address?.street],
            city: [address?.city],
            state: [address?.state],
            zipCode: [address?.zipCode],
            id: [address?.id]
        });
    }

    hasAddress(address: Address): boolean {
        return !!(address?.street || address?.city || address?.state || address?.zipCode);
    }

    createAddresses(defaultAddresses: Address[]): FormArray {
        defaultAddresses = defaultAddresses.filter(address => this.hasAddress(address));
        return this.fb.array(
            defaultAddresses.length > 0 ?
                defaultAddresses.map(address => this.createAddress(address)) :
                [this.createAddress()]
        );
    }

    createHours(defaultHours: any[] = []): FormArray {
        return this.fb.array(
            businessDays().map(day => {
                const defaultHour = defaultHours.find(h => h?.day?.toLowerCase?.() === day?.day?.toLowerCase?.()) || { open: 'Closed', close: 'Closed' };
                return this.createHour(day, defaultHour);
            })
        );
    }

    createHour(day, defaultHour = { open: 'Closed', close: 'Closed' }): FormGroup {
        const openTime = defaultHour.open || 'Closed';
        const closeTime = defaultHour.close || 'Closed';

        return this.fb.group({
            day: day.day,
            open: new FormControl(this.convert24To12HourFormat(openTime, 'open')),
            close: new FormControl(this.convert24To12HourFormat(closeTime, 'close'))
        });
    }

    // Convenience methods for backward compatibility and clarity
    createBusinessHours(defaultHours: any[] = []): FormArray {
        return this.createHours(defaultHours);
    }

    createCustomHours(defaultHours: any[] = []): FormArray {
        return this.fb.array(
            businessDays().map(day => {
                const existingHour = defaultHours.find(h => h.day?.toLowerCase?.() === day.day?.toLowerCase?.() || h.day?.toLowerCase?.() === day.day?.toLowerCase?.());
                if (existingHour) {
                    return this.createHour(day, existingHour);
                } else {
                    return this.createHour(day, { open: '08:00', close: '17:00' });
                }
            })
        );
    }

    createFaqs(faqs: TrainingFaq[]): FormArray {
        return this.fb.array(faqs.length > 0 ? faqs.map(faq => this.createFaq(faq)) : []);
    }

    createBlockedPhoneNumbers(phoneNumbers: PhoneNumber[]): FormArray {
        return this.fb.array(phoneNumbers.length > 0 ? phoneNumbers.map(phoneNumber => this.createBlockedPhoneNumber(phoneNumber)) : []);
    }

    createBlockedPhoneNumber(phoneNumber: PhoneNumber = { number: '' }): FormGroup {
        return this.fb.group({
            number: [formatPhoneNumber(phoneNumber.number), [Validators.required]],
            countryPrefix: '1',
            id: phoneNumber.id,
            reason: phoneNumber.reason ?? null
        });
    }

    createBusinessPhoneNumber(phoneNumber: BusinessPhoneNumber = { id: null, number: '', isPrimary: false, type: BusinessPhoneNumberType.Test, isActive: true, countryPrefix: '1' }, isRequired: boolean = false): FormGroup {
        const validators = isRequired ? [Validators.required] : [];
        return this.fb.group({
            id: [phoneNumber.id],
            number: [formatPhoneNumber(phoneNumber.number), validators],
            isPrimary: [phoneNumber.isPrimary || false],
            type: [phoneNumber.type || BusinessPhoneNumberType.Test],
            isActive: [phoneNumber.isActive !== undefined ? phoneNumber.isActive : true],
            countryPrefix: [phoneNumber.countryPrefix || '1']
        });
    }

    createTransferDepartment(department: TransferDepartment = {
        type: TransferDepartmentType.Simple,
        department: '',
        destinations: [{ number: '', label: '' }],
        availabilityType: TransferDepartmentAvailabilityType.Always,
        customHours: []
    }): FormGroup {
        const destinations = (department.destinations && department.destinations.length > 0)
            ? department.destinations
            : [{ number: '', label: '' }];
        const isWarm = department?.type === TransferDepartmentType.Live;
        // Warm transfers historically asked for name + reason via a hardcoded
        // prompt rule. That rule now reads from `collectInfoBeforeTransfer` +
        // `collectInfoPrompt`, so new warm departments default to ON with the
        // canonical prompt to preserve behavior. Existing rows come back from
        // the API with the same virtual default already applied.
        const defaultWarmCollectInfo = isWarm;
        const defaultCollectInfoPrompt = isWarm ? "Caller's first name and reason for calling" : '';
        return this.fb.group({
            type: department?.type || TransferDepartmentType.Simple,
            destinations: this.fb.array(destinations.map(destination => this.createDestination(destination))),
            isWarmTransfer: department?.hasOwnProperty('isWarmTransfer') ? department?.isWarmTransfer : (department?.type === TransferDepartmentType.Live ? true : false),
            id: department.id,
            department: [department.department || '', [Validators.required, Validators.maxLength(250)]],
            availabilityType: [department?.availabilityType || TransferDepartmentAvailabilityType.Always],
            customHours: this.createCustomHours(department?.customHours || []),
            holdMusic: [department?.holdMusic || department?.configuration?.holdMusic || 'soft_guitar'],
            collectInfoBeforeTransfer: [department?.collectInfoBeforeTransfer ?? defaultWarmCollectInfo],
            collectInfoPrompt: [department?.collectInfoPrompt ?? defaultCollectInfoPrompt, [Validators.maxLength(1000)]],
            triggerDescription: [department?.triggerDescription || '', [Validators.maxLength(1000)]]
        });
    }

    createTextLink(template: TextLink = { message: '', url: '', department: '' }, businessTitle?: string): FormGroup {
        return this.fb.group({
            message: [template.message || `${businessTitle || ''}: Here’s the link you requested`, [Validators.required, Validators.maxLength(150), noEmojiValidator()]],
            url: [template.url || '', [Validators.required, Validators.pattern(HTTPS_URL_PATTERN), Validators.maxLength(250)]],
            id: template.id || null,
            department: [template.department || '', [Validators.required, Validators.maxLength(250)]]
        });
    }

    createDestination(destination: { number: string, label: string, countryPrefix?: string }): FormGroup {
        return this.fb.group({
            number: [formatPhoneNumber(destination?.number), [Validators.required]],
            countryPrefix: destination?.countryPrefix || '1',
            label: [destination?.label]
        });
    }


    createLanguages(languages: string[] = [], supportedLanguages: { value: string, label: string }[]): FormArray {
        return this.fb.array(supportedLanguages.map(language => this.createLanguage(language, languages.includes(language.value) || language.value === 'en')));
    }

    createLanguage(language: { value: string, label: string }, isEnabled: boolean): FormGroup {
        return this.fb.group({ value: language.value, label: language.label, isEnabled: isEnabled });
    }

    createCustomQuestions(customQuestions: CustomQuestion[], isRequired: boolean = true): FormArray {
        return this.fb.array(customQuestions.length > 0 ? customQuestions.map(customQuestion => this.createCustomQuestion(customQuestion, isRequired)) : []);
    }

    createCustomQuestion(customQuestion: CustomQuestion = { question: '' }, isRequired: boolean = true): FormGroup {
        return this.fb.group({
            question: [customQuestion.question, [Validators.required, Validators.maxLength(150)]],
        });
    }

    createScenarios(scenarios: MessageScenario[] = []): FormArray {
        return this.fb.array(scenarios.length > 0 ? scenarios.map(scenario => this.createScenario(scenario)) : []);
    }

    createScenario(scenario: MessageScenario = { title: '', text: '' }): FormGroup {
        return this.fb.group({
            id: [scenario.id || null],
            title: [scenario.title || '', [Validators.required, Validators.maxLength(60)]],
            text: [scenario.text || '', [Validators.required, Validators.maxLength(500)]],
            priority: [scenario.priority ?? null],
            suggested: [scenario.suggested || false]
        });
    }

    createFaq(faq: TrainingFaq = { answer: '', question: '' }): FormGroup {
        return this.fb.group({
            question: [faq.question, [Validators.required, Validators.maxLength(250)]],
            answer: [faq.answer, [Validators.required, Validators.maxLength(500)]],
            id: [faq.id]
        });
    }

    // Expired entries are left out, so the next publish prunes them (ENG-2393).
    createSpecialHours(businessInformation: AdditionalProperties): SpecialHours[] {
        return activeSpecialHours(businessInformation?.specialHours, todayInTimezone(businessInformation?.timezone));
    }

    convert24To12HourFormat(time: string, context: 'open' | 'close' = 'open'): string {
        if (!time) return 'Closed';
        if (time === 'Closed') return time;
        if (time.toLowerCase().includes('pm') || time.toLowerCase().includes('am')) return time.toLowerCase();
        const [hour, minute] = time.split(':').map(Number);

        const defaultHour = context === 'open' ? 8 : 17;
        const validHour = isNaN(hour) ? defaultHour : hour;
        const validMinute = isNaN(minute) ? 0 : minute;

        const period = validHour < 12 ? ' AM' : ' PM';
        const formattedHour = validHour % 12 === 0 ? 12 : validHour % 12;
        return `${formattedHour}:${validMinute < 10 ? '0' + validMinute : validMinute}${period?.toLowerCase()}`;
    }

    convert12To24HourFormat(time: string, context: 'open' | 'close' = 'open'): string {
        if (!time) return 'Closed';
        if (time === 'Closed') return time;
        const [timePart, period] = time.split(/(am|pm)/i).filter(Boolean);
        const [hour, minute] = timePart.split(':').map(Number);

        const defaultHour = context === 'open' ? 8 : 5;
        const defaultPeriod = context === 'open' ? 'am' : 'pm';
        const validHour = isNaN(hour) ? defaultHour : hour;
        const validMinute = isNaN(minute) ? 0 : minute;
        const validPeriod = period?.toLowerCase?.() || defaultPeriod;

        let formattedHour = validPeriod === 'am' ? validHour : validHour + 12;
        if (formattedHour === 24) formattedHour = 12;
        if (formattedHour === 12 && validPeriod === 'am') formattedHour = 0;
        return `${formattedHour < 10 ? '0' + formattedHour : formattedHour}:${validMinute < 10 ? '0' + validMinute : validMinute}`;
    }

    createTransferDepartments(departments: TransferDepartment[]): FormArray {
        return this.fb.array(departments.map(department => this.createTransferDepartment(department)));
    }

    createTextLinks(templates: TextLink[]): FormArray {
        return this.fb.array(templates.map(department => this.createTextLink(department)));
    }

    createPhoneNumbers(defaultPhoneNumbers: BusinessPhoneNumber[]): FormArray {
        const primaryPhoneNumber = defaultPhoneNumbers.find(p => p.type === BusinessPhoneNumberType.Main);
        const phoneNumbers = [{ isActive: true, number: formatPhoneNumber(primaryPhoneNumber?.number), type: BusinessPhoneNumberType.Main, id: primaryPhoneNumber?.id, isPrimary: true }];
        return this.fb.array(phoneNumbers.map(phoneNumber => this.fb.group(phoneNumber)));
    }



    createServices(defaultServices: Service[]) {
        return this.fb.array(defaultServices.length > 0 ?
            defaultServices.map(service => this.createService(service)) :
            []);
    }

    createAreas(defaultAreas: Area[]) {
        return this.fb.array(defaultAreas.length > 0 ?
            defaultAreas.map(area => this.createArea(area)) :
            []);
    }

    /**
     * Extracts string values from service areas (handles both string[] and Area[] from API)
     */
    extractServiceAreaStrings(areas: (string | Area)[]): string[] {
        if (!areas) return [];
        return areas.map(area => typeof area === 'string' ? area : area.name).filter(Boolean);
    }

    createAgentKnowledge(knowledge?: AgentKnowledge): FormGroup {
        return this.fb.group({
            content: [knowledge?.content || '', [Validators.maxLength(3000)]],
            isEdited: [knowledge?.isEdited || false],
            sourceType: [knowledge?.sourceType || 'scraped'],
            lastUpdated: [knowledge?.lastUpdated || null]
        });
    }

    createSmsWidget(config: { primaryColor?: string; position?: 'bottom-right' | 'bottom-left'; avatar?: string; greetingMessage?: string; chatGreetingMessage?: string; widgetOpenMode?: string; widgetOpenDelay?: number } = {}): FormGroup {
        return this.fb.group({
            primaryColor: [config.primaryColor || '#2DD4BF', Validators.required],
            position: [config.position || 'bottom-right', Validators.required],
            avatar: [config.avatar || ''],
            greetingMessage: [config.greetingMessage || '', Validators.maxLength(160)],
            chatGreetingMessage: [config.chatGreetingMessage || '', Validators.maxLength(160)],
            widgetOpenMode: [config.widgetOpenMode || 'immediately', Validators.required],
            widgetOpenDelay: [config.widgetOpenDelay || 5]
        });
    }

    createAttendeeEmails(emails: string[] = []): FormArray {
        return this.fb.array(emails.map(email => this.createAttendeeEmail(email)));
    }

    createAttendeeEmail(email: string = ''): FormControl {
        return new FormControl(email, [Validators.required, Validators.email]);
    }

    // Attendees default to the calendar owner's email only when they've never been
    // configured (null/undefined from the API). An explicit empty array means the
    // user removed the owner email, so it must NOT be re-seeded on the next open.
    resolveAttendeeEmails(agent: Agent): string[] {
        const configured = agent?.calendar?.config?.attendeeEmails;
        if (configured != null) {
            return configured;
        }
        const ownerEmail = agent?.calendar?.details?.account;
        return ownerEmail ? [ownerEmail] : [];
    }

    // Asking the caller for their email is on unless the merchant explicitly
    // turned it off, so anything but a stored false reads as on.
    resolveCollectCallerEmail(agent: Agent): boolean {
        return agent?.calendar?.config?.collectCallerEmail !== false;
    }

    createAlternativeGreetingCustomHours(defaultHours: AlternativeGreetingCustomHour[] = []): FormArray {
        return this.fb.array(
            businessDays().map(day => {
                const existingHour = defaultHours.find(h => h.day?.toLowerCase?.() === day.day?.toLowerCase?.());
                if (existingHour) {
                    return this.createHour(day, existingHour);
                } else {
                    return this.createHour(day, { open: 'Closed', close: 'Closed' });
                }
            })
        );
    }

    // Conflict calendars default to the booking calendar only when they have never
    // been configured (null/undefined from the API). An explicit empty array means
    // the merchant cleared the extra calendars, so none are re-seeded. The booking
    // calendar itself is always checked for conflicts and cannot be turned off.
    resolveConflictCalendarIds(agent: Agent): string[] {
        const configured = agent?.calendar?.config?.conflictCalendarIds;
        const bookingCalendarId = agent?.calendar?.config?.calendarId;
        const ids = configured != null ? [...configured] : (bookingCalendarId ? [bookingCalendarId] : []);
        if (bookingCalendarId && !ids.includes(bookingCalendarId)) {
            ids.unshift(bookingCalendarId);
        }
        return ids;
    }

    createConflictCalendarIds(ids: string[] = []): FormArray {
        return this.fb.array(ids.map(id => new FormControl(id)));
    }

    createAvailabilityCustomHours(defaultHours: CalendarCustomHour[] = []): FormArray {
        return this.createCustomHours(defaultHours || []);
    }

    createDateOverrides(overrides: CalendarDateOverride[] = []): FormArray {
        return this.fb.array((overrides || []).map(override => this.createDateOverride(override)));
    }

    createDateOverride(override?: CalendarDateOverride): FormGroup {
        const times = this.resolveDateOverrideTimes(override);
        return this.fb.group({
            startDate: [this.parseDateString(override?.startDate)],
            endDate: [this.parseDateString(override?.endDate)],
            type: [this.resolveDateOverrideState(override)],
            open: new FormControl(times.open),
            close: new FormControl(times.close)
        });
    }

    // Rebuilds a row from an already form-shaped value (date structs and 12 hour
    // times), which is what the modal's cancel snapshot holds.
    createDateOverrideFromFormValue(row: DateOverrideFormValue): FormGroup {
        return this.fb.group({
            startDate: [row?.startDate || null],
            endDate: [row?.endDate || null],
            type: [this.resolveDateOverrideState(row)],
            open: new FormControl(row?.open || '9:00 am'),
            close: new FormControl(row?.close || '5:00 pm')
        });
    }

    // `type` is the contract's discriminator, so it wins whenever it is present.
    // The time sentinels are only a fallback for rows that predate the field or
    // arrive without it: 'Closed' means unavailable and the midnight-to-11:45pm
    // pair means all day, the same way the alternative greeting rows encode it.
    resolveDateOverrideState(row?: { type?: string, open?: string, close?: string }): DateOverrideState {
        if (row?.type === 'hours' || row?.type === 'all_day' || row?.type === 'unavailable') {
            return row.type;
        }
        if (row?.open === 'Closed' || row?.close === 'Closed') {
            return 'unavailable';
        }
        if (row?.open === '12:00 am' && row?.close === '11:45 pm') {
            return 'all_day';
        }
        return 'hours';
    }

    // The times a row carries for a given type. Kept next to the type so the two
    // never drift apart when a merchant switches a row between modes.
    dateOverrideTimesForState(state: DateOverrideState): { open: string, close: string } {
        if (state === 'unavailable') {
            return { open: 'Closed', close: 'Closed' };
        }
        if (state === 'all_day') {
            return { open: '12:00 am', close: '11:45 pm' };
        }
        return { open: '9:00 am', close: '5:00 pm' };
    }

    buildDateOverridesPayload(rows: DateOverrideFormValue[] = []): CalendarDateOverride[] {
        return (rows || [])
            .filter(row => !!this.formatDateStruct(row?.startDate))
            .map(row => {
                const type: CalendarDateOverrideType = this.resolveDateOverrideState(row);
                const payload: CalendarDateOverride = { startDate: this.formatDateStruct(row.startDate), type };
                const endDate = this.formatDateStruct(row.endDate);
                if (endDate) {
                    payload.endDate = endDate;
                }
                if (type !== 'unavailable') {
                    payload.open = this.convert12To24HourFormat(row.open, 'open');
                    payload.close = this.convert12To24HourFormat(row.close, 'close');
                }
                return payload;
            });
    }

    buildAvailabilityCustomHoursPayload(hours: CalendarCustomHour[] = []): CalendarCustomHour[] {
        return (hours || []).map(hour => ({
            day: hour.day,
            open: this.convert12To24HourFormat(hour.open, 'open'),
            close: this.convert12To24HourFormat(hour.close, 'close')
        }));
    }

    buildCalendarConfigPayload(scheduling: SchedulingFormValue): CalendarConfig {
        const availabilityType: CalendarAvailabilityType = scheduling?.availabilityType || 'business_hours';
        const payload: CalendarConfig = {
            meetingTitle: scheduling?.meetingTitle,
            meetingDuration: scheduling?.meetingDuration,
            meetingEventType: scheduling?.meetingEventType,
            calendarId: scheduling?.calendarId,
            attendeeEmails: scheduling?.attendeeEmails || [],
            collectCallerEmail: scheduling?.collectCallerEmail !== false,
            conflictCalendarIds: scheduling?.conflictCalendarIds || [],
            availabilityType,
            maxDaysInAdvance: Number(scheduling?.maxDaysInAdvance),
            minLeadTimeHours: Number(scheduling?.minLeadTimeHours),
            dateOverrides: this.buildDateOverridesPayload(scheduling?.dateOverrides)
        };

        // The API merges calendar_config key by key and replaces arrays wholesale,
        // so sending an empty customHours while on business hours would delete a
        // schedule the merchant configured earlier. Leaving the key out entirely
        // keeps the stored schedule intact for when they switch back to Custom.
        if (availabilityType === 'custom') {
            payload.customHours = this.buildAvailabilityCustomHoursPayload(scheduling?.customHours);
        }

        return payload;
    }

    parseDateString(value?: string | null): DateStruct | null {
        if (!value) {
            return null;
        }
        const [year, month, day] = `${value}`.split('-').map(Number);
        if (!year || !month || !day) {
            return null;
        }
        return { year, month, day };
    }

    formatDateStruct(value?: DateStruct | null): string | null {
        if (!value?.year || !value?.month || !value?.day) {
            return null;
        }
        const month = value.month < 10 ? `0${value.month}` : `${value.month}`;
        const day = value.day < 10 ? `0${value.day}` : `${value.day}`;
        return `${value.year}-${month}-${day}`;
    }

    private resolveDateOverrideTimes(override?: CalendarDateOverride): { open: string, close: string } {
        const state = this.resolveDateOverrideState(override);
        if (!override || state !== 'hours') {
            return this.dateOverrideTimesForState(state);
        }
        return {
            open: this.convert24To12HourFormat(override.open || '09:00', 'open'),
            close: this.convert24To12HourFormat(override.close || '17:00', 'close')
        };
    }

    normalizeDomain(input: string | SmsWidgetAllowedDomain): SmsWidgetAllowedDomain {
        if (typeof input === 'string') {
            return { domain: input, autoAdded: false };
        }
        return { domain: input.domain, autoAdded: input.autoAdded || false };
    }

    createSmsWidgetAllowedDomains(domains: (string | SmsWidgetAllowedDomain)[] = []): FormArray {
        return this.fb.array(domains.map(d => this.createSmsWidgetAllowedDomain(this.normalizeDomain(d))));
    }

    createSmsWidgetAllowedDomain(domain: SmsWidgetAllowedDomain = { domain: '', autoAdded: false }): FormGroup {
        return this.fb.group({
            domain: [domain.domain, Validators.required],
            autoAdded: [domain.autoAdded || false]
        });
    }

    areEqual(currentValues: any, initialValues: any): boolean {
        return areEqual(currentValues, initialValues);
    }

    initializeAgentForm(agent: Agent, faqs: TrainingFaq[], customQuestions: CustomQuestion[], supportedLanguages: { value: string, label: string }[], scenarios: MessageScenario[] = []): FormGroup {
        const business: Business = agent?.business;
        const hasLegalDisclaimerEnabled = agent?.configuration?.hasOwnProperty('hasLegalDisclaimerEnabled') ? agent?.configuration?.hasLegalDisclaimerEnabled : true;
        const customGreeting = !hasLegalDisclaimerEnabled ? agent?.configuration?.customGreeting?.replace(':legal_disclaimer', '') : agent?.configuration?.customGreeting;
        const bookingMessageValidators = [Validators.maxLength(2048), noEmojiValidator()];

        return this.fb.group({
            profile: this.fb.group({
                tone: [agent?.configuration?.tone, Validators.required],
                title: [agent?.title, Validators.required],
                voiceId: [agent?.configuration?.voiceId || 'rosie', Validators.required],
                backgroundNoise: [(agent?.configuration?.backgroundNoise || BackgroundNoiseType.Office).toLowerCase(), Validators.required],
                voiceSpeed: [agent?.configuration?.voiceSpeed || 'average', Validators.required],
            }),
            languages: this.createLanguages(agent?.configuration?.languages || [], supportedLanguages),
            configuration: this.fb.group({
                hasCustomQuestionsForNotesEnabled: true,
                appointmentLengthMinutes: agent?.configuration?.appointmentLengthMinutes || null,
                askForName: agent?.configuration?.askForName ?? true,
                nameFormat: agent?.configuration?.nameFormat || 'full_name',
                requestCallerSpellName: agent?.configuration?.requestCallerSpellName ?? false,
            }),
            training: this.createFaqs(faqs || []),
            capabilities: this.createCustomQuestions(customQuestions.filter(q => !(q as any).type || (q as any).type === 'note') || []),
            scenarios: this.createScenarios(scenarios || []),
            spam: this.fb.group({
                hasBlock1800NumbersEnabled: agent?.configuration?.hasBlock_1800NumbersEnabled || false,
                hasSmartSpamDetectionEnabled: agent?.configuration?.hasSmartSpamDetectionEnabled || false,
                hasBlockCallsBeforeConnectEnabled: agent?.configuration?.hasBlockCallsBeforeConnectEnabled ?? true
            }),
            blockedPhoneNumbers: this.createBlockedPhoneNumbers(agent?.blockedPhoneNumbers || []),
            customGreeting: this.fb.group({ message: [customGreeting || '', Validators.maxLength(250)], hasLegalDisclaimerEnabled: hasLegalDisclaimerEnabled, legalDisclaimer: agent.greeting.legalDisclaimer || '', hasCustomGreetingEnabled: agent?.configuration?.hasCustomGreetingEnabled || false }),
            booking: this.fb.group({ id: agent.bookingMessage?.id, message: [agent.bookingMessage?.message || `${business?.title || ''}: Schedule an appointment with the following link`, bookingMessageValidators], url: [agent.bookingMessage?.url || '', [Validators.pattern(HTTPS_URL_PATTERN), agent.bookingMessage?.message?.length <= 150 ? Validators.maxLength(150) : Validators.maxLength(250), Validators.required]] }),
            scheduling: this.fb.group({
                calendarId: [agent?.calendar?.config?.calendarId || null],
                meetingTitle: [agent?.calendar?.config?.meetingTitle || 'New Appointment', Validators.required],
                meetingDuration: [agent?.calendar?.config?.meetingDuration || 60, Validators.required],
                meetingEventType: [agent?.calendar?.config?.meetingEventType || agent?.calendar?.details?.eventTypes?.[0]?.uri || null],
                bookingQuestions: this.createCustomQuestions(customQuestions.filter(q => (q as any).type === 'calendar_event'), false),
                attendeeEmails: this.createAttendeeEmails(this.resolveAttendeeEmails(agent)),
                collectCallerEmail: [this.resolveCollectCallerEmail(agent)],
                conflictCalendarIds: this.createConflictCalendarIds(this.resolveConflictCalendarIds(agent)),
                availabilityType: [agent?.calendar?.config?.availabilityType || 'business_hours'],
                customHours: this.createAvailabilityCustomHours(agent?.calendar?.config?.customHours || []),
                maxDaysInAdvance: [agent?.calendar?.config?.maxDaysInAdvance ?? DEFAULT_MAX_DAYS_IN_ADVANCE, [Validators.required, Validators.min(1)]],
                minLeadTimeHours: [agent?.calendar?.config?.minLeadTimeHours ?? DEFAULT_MIN_LEAD_TIME_HOURS, [Validators.required, Validators.min(0)]],
                dateOverrides: this.createDateOverrides(agent?.calendar?.config?.dateOverrides || [])
            }),
            hasAppointmentsEnabled: [agent?.configuration?.hasAppointmentsEnabled || false],
            hasAppointmentSchedulingEnabled: [agent?.configuration?.hasAppointmentSchedulingEnabled || false],
            smsWidget: this.createSmsWidget(agent?.configuration?.smsWidget),
            smsWidgetAllowedDomains: this.createSmsWidgetAllowedDomains(agent?.configuration?.smsWidgetAllowedDomains || []),
            alternativeGreeting: this.fb.group({
                hasAlternativeGreetingEnabled: [agent?.configuration?.hasAlternativeGreetingEnabled || false],
                alternativeGreeting: [agent?.configuration?.alternativeGreeting || '', Validators.maxLength(250)],
                alternativeGreetingHoursType: [agent?.configuration?.alternativeGreetingHoursType || 'after_hours'],
                alternativeGreetingCustomHours: this.createAlternativeGreetingCustomHours(agent?.configuration?.alternativeGreetingCustomHours || []),
                hasAlternativeGreetingLegalDisclaimerEnabled: [agent?.configuration?.hasAlternativeGreetingLegalDisclaimerEnabled ?? true]
            })
        });
    }

    initializeBusinessForm(agent: Agent): FormGroup {
        const businessInformation: AdditionalProperties = agent?.business?.information?.additionalProperties;
        const business: Business = agent?.business;
        const primaryPhoneNumber = business?.phoneNumbers?.find(p => p.type === BusinessPhoneNumberType.Main)
        const timezone = timeZones.find(tz => tz.value === businessInformation?.timezone)?.label || businessInformation?.timezone;
        return this.fb.group({
            details: this.fb.group({
                title: [business?.title, Validators.required],
                titlePronunciation: [businessInformation?.titlePronunciation || ''],
                email: [businessInformation?.email, Validators.email],
                summary: [businessInformation?.summary],
                addresses: this.createAddresses(businessInformation?.addresses || []),
                hasCustomSummaryEnabled: agent.configuration.hasCustomSummaryEnabled || false,
                customSummary: [agent.configuration.customSummary || businessInformation?.summary, Validators.maxLength(500)],
                primaryBusinessPhoneNumber: this.createBusinessPhoneNumber({
                    id: primaryPhoneNumber?.id,
                    number: primaryPhoneNumber?.number || '',
                    isPrimary: true,
                    type: BusinessPhoneNumberType.Main,
                    isActive: true,
                    countryPrefix: primaryPhoneNumber?.countryPrefix || '1'
                }, true),
            }),
            schedule: this.fb.group({
                hours: this.createBusinessHours(businessInformation?.businessHours || []),
                timezone: timezone,
                specialHours: this.fb.control(this.createSpecialHours(businessInformation))
            }),
            services: this.fb.control(business.services?.map(service => service?.name) || []),
            areas: this.fb.control(this.extractServiceAreaStrings(businessInformation?.serviceAreas)),
            agentKnowledge: {
                content: businessInformation?.additionalContext || '',
                isEdited: !!businessInformation?.additionalContext,
                sourceType: businessInformation?.additionalContext ? 'manual' : 'scraped',
                lastUpdated: null
            },
            takeMessage: [false],
            transferCalls: this.createTransferDepartments(business?.transferDepartments || []),
            textLinks: this.createTextLinks(business?.textALinkMessages || []),
            sourceTraining: this.fb.group({ placeId: businessInformation?.placeId, url: [businessInformation?.url || '', Validators.pattern(WEBSITE_URL_PATTERN)], name: businessInformation?.name }),
        });
    }

    createAgentFormValue(agent: Agent, faqs: TrainingFaq[], customQuestions: CustomQuestion[], scenarios: MessageScenario[] = []): any {
        const hasLegalDisclaimerEnabled = agent?.configuration?.hasOwnProperty('hasLegalDisclaimerEnabled') ? agent?.configuration?.hasLegalDisclaimerEnabled : true;
        const customGreeting = !hasLegalDisclaimerEnabled
            ? agent?.configuration?.customGreeting?.replace(':legal_disclaimer', '')
            : agent?.configuration?.customGreeting;

        const updatedValue = {
            profile: {
                tone: agent?.configuration?.tone || null,
                title: agent?.title || '',
                voiceId: agent?.configuration?.voiceId || 'rosie',
                backgroundNoise: (agent?.configuration?.backgroundNoise || BackgroundNoiseType.Office).toLowerCase(),
                voiceSpeed: agent?.configuration?.voiceSpeed || 'average',
            },
            configuration: {
                hasCustomQuestionsForNotesEnabled: true,
                appointmentLengthMinutes: agent?.configuration?.appointmentLengthMinutes || null,
                askForName: agent?.configuration?.askForName ?? true,
                nameFormat: agent?.configuration?.nameFormat || 'full_name',
                requestCallerSpellName: agent?.configuration?.requestCallerSpellName ?? false,
            },
            training: faqs || [],
            capabilities: customQuestions.filter(q => !(q as any).type || (q as any).type === 'note') || [],
            scenarios: (scenarios || []).map(s => ({
                id: s.id || null,
                title: s.title || '',
                text: s.text || '',
                priority: s.priority ?? null,
                suggested: s.suggested || false
            })),
            spam: {
                hasBlock1800NumbersEnabled: agent?.configuration?.hasBlock_1800NumbersEnabled || false,
                hasSmartSpamDetectionEnabled: agent?.configuration?.hasSmartSpamDetectionEnabled || false,
                hasBlockCallsBeforeConnectEnabled: agent?.configuration?.hasBlockCallsBeforeConnectEnabled ?? true
            },
            blockedPhoneNumbers: agent.blockedPhoneNumbers || [],
            customGreeting: {
                message: customGreeting || agent?.greeting?.default,
                hasLegalDisclaimerEnabled,
                legalDisclaimer: agent.greeting.legalDisclaimer || '',
                hasCustomGreetingEnabled: agent?.configuration?.hasCustomGreetingEnabled || false
            },
            booking: {
                id: agent.bookingMessage?.id || null,
                message:
                    agent.bookingMessage?.message ||
                    `${agent?.business?.title || ''}: Schedule an appointment with the following link`,
                url: agent.bookingMessage?.url || '',
            },
            scheduling: {
                calendarId: agent?.calendar?.config?.calendarId || null,
                meetingTitle: agent?.calendar?.config?.meetingTitle || 'New Appointment',
                meetingDuration: agent?.calendar?.config?.meetingDuration || 60,
                meetingEventType: agent?.calendar?.config?.meetingEventType || agent?.calendar?.details?.eventTypes?.[0]?.uri || null,
                bookingQuestions: customQuestions.filter(q => (q as any).type === 'calendar_event') || [],
                attendeeEmails: this.resolveAttendeeEmails(agent),
                collectCallerEmail: this.resolveCollectCallerEmail(agent),
                conflictCalendarIds: this.resolveConflictCalendarIds(agent),
                availabilityType: agent?.calendar?.config?.availabilityType || 'business_hours',
                customHours: (agent?.calendar?.config?.customHours || []).map(hour => ({
                    day: hour.day,
                    open: this.convert24To12HourFormat(hour.open, 'open'),
                    close: this.convert24To12HourFormat(hour.close, 'close')
                })),
                maxDaysInAdvance: agent?.calendar?.config?.maxDaysInAdvance ?? DEFAULT_MAX_DAYS_IN_ADVANCE,
                minLeadTimeHours: agent?.calendar?.config?.minLeadTimeHours ?? DEFAULT_MIN_LEAD_TIME_HOURS,
                dateOverrides: agent?.calendar?.config?.dateOverrides || []
            },
            hasAppointmentsEnabled: agent?.configuration?.hasAppointmentsEnabled || false,
            hasAppointmentSchedulingEnabled: agent?.configuration?.hasAppointmentSchedulingEnabled || false,
            smsWidget: {
                primaryColor: agent?.configuration?.smsWidget?.primaryColor || '#2DD4BF',
                position: agent?.configuration?.smsWidget?.position || 'bottom-right',
                avatar: agent?.configuration?.smsWidget?.avatar || '',
                greetingMessage: agent?.configuration?.smsWidget?.greetingMessage || '',
                chatGreetingMessage: agent?.configuration?.smsWidget?.chatGreetingMessage || '',
                widgetOpenMode: agent?.configuration?.smsWidget?.widgetOpenMode || 'immediately',
                widgetOpenDelay: agent?.configuration?.smsWidget?.widgetOpenDelay || 5
            },
            smsWidgetAllowedDomains: (agent?.configuration?.smsWidgetAllowedDomains || []).map(d => this.normalizeDomain(d)),
            alternativeGreeting: {
                hasAlternativeGreetingEnabled: agent?.configuration?.hasAlternativeGreetingEnabled || false,
                alternativeGreeting: agent?.configuration?.alternativeGreeting || '',
                alternativeGreetingHoursType: agent?.configuration?.alternativeGreetingHoursType || 'after_hours',
                alternativeGreetingCustomHours: (agent?.configuration?.alternativeGreetingCustomHours || []).map(hour => ({
                    day: hour.day,
                    open: this.convert24To12HourFormat(hour.open, 'open'),
                    close: this.convert24To12HourFormat(hour.close, 'close')
                })),
                hasAlternativeGreetingLegalDisclaimerEnabled: agent?.configuration?.hasAlternativeGreetingLegalDisclaimerEnabled ?? true
            }
        };
        return updatedValue;
    }

    createBusinessFormValue(agent: Agent): any {
        const businessInformation: AdditionalProperties = agent?.business?.information?.additionalProperties;
        const business: Business = agent?.business;
        const primaryPhoneNumber = business?.phoneNumbers?.find(p => p.type === BusinessPhoneNumberType.Main);
        const timezone = timeZones.find(tz => tz.value === businessInformation?.timezone)?.label || businessInformation?.timezone;
        business.transferDepartments.forEach(department => {
            department.isWarmTransfer = department.type === TransferDepartmentType.Live;
            // Set default availability type if not present
            if (!department.availabilityType) {
                department.availabilityType = TransferDepartmentAvailabilityType.Always;
            }
            // Format custom hours if present
            if (department.customHours) {
                department.customHours = department.customHours.map(hour => ({
                    day: hour.day,
                    open: this.convert24To12HourFormat(hour.open, 'open'),
                    close: this.convert24To12HourFormat(hour.close, 'close')
                }));
            }
            // Round-trip new fields so business switching does not leave stale values
            department.collectInfoBeforeTransfer = department.collectInfoBeforeTransfer || false;
            department.collectInfoPrompt = department.collectInfoPrompt || '';
            department.triggerDescription = department.triggerDescription || '';
            department.holdMusic = department.holdMusic || department.configuration?.holdMusic || 'soft_guitar';
        });
        return {
            details: {
                title: business?.title || '',
                titlePronunciation: businessInformation?.titlePronunciation || '',
                email: businessInformation?.email || '',
                summary: businessInformation?.summary || '',
                hasCustomSummaryEnabled: agent?.configuration?.hasCustomSummaryEnabled || false,
                customSummary: agent?.configuration?.customSummary || '',
                addresses: businessInformation?.addresses?.filter((address) => this.hasAddress(address)) || [],
                primaryBusinessPhoneNumber: {
                    id: primaryPhoneNumber?.id,
                    number: formatPhoneNumber(primaryPhoneNumber?.number || ''),
                    isPrimary: true,
                    type: BusinessPhoneNumberType.Main,
                    isActive: true,
                    countryPrefix: primaryPhoneNumber?.countryPrefix || '1'
                },
            },
            schedule: {
                hours: businessInformation?.businessHours?.map(hour => ({
                    day: hour.day,
                    open: this.convert24To12HourFormat(hour.open, 'open'),
                    close: this.convert24To12HourFormat(hour.close, 'close')
                })) || [],
                timezone: timezone || null,
                specialHours: this.createSpecialHours(businessInformation)
            },
            services: business.services?.map(service => service?.name) || [],
            areas: this.extractServiceAreaStrings(businessInformation?.serviceAreas),
            agentKnowledge: {
                content: businessInformation?.additionalContext || '',
                isEdited: !!businessInformation?.additionalContext,
                sourceType: businessInformation?.additionalContext ? 'manual' : 'scraped',
                lastUpdated: null
            },
            transferCalls: business.transferDepartments || [],
            textLinks: business.textALinkMessages || [],
            takeMessage: false,
            sourceTraining: {
                placeId: businessInformation?.placeId || '',
                url: businessInformation?.url || '',
                name: businessInformation?.name || ''
            }
        };
    }

}

export type DateOverrideState = CalendarDateOverrideType;

export interface DateOverrideFormValue {
    startDate: DateStruct | null;
    endDate?: DateStruct | null;
    type?: string;
    open?: string;
    close?: string;
}

export interface SchedulingFormValue {
    meetingTitle?: string;
    meetingDuration?: number;
    meetingEventType?: string | null;
    calendarId?: string | null;
    attendeeEmails?: string[];
    collectCallerEmail?: boolean;
    conflictCalendarIds?: string[];
    availabilityType?: CalendarAvailabilityType;
    customHours?: CalendarCustomHour[];
    maxDaysInAdvance?: number | string;
    minLeadTimeHours?: number | string;
    dateOverrides?: DateOverrideFormValue[];
}

export interface DateStruct {
    year: number;
    month: number;
    day: number;
}

export const DEFAULT_MAX_DAYS_IN_ADVANCE = 60;
export const DEFAULT_MIN_LEAD_TIME_HOURS = 4;

export function noEmojiValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const emojiRegex = /(?:\p{Extended_Pictographic})/u;
        const hasEmoji = emojiRegex.test(control.value);
        return hasEmoji ? { emojiNotAllowed: true } : null;
    };
}
